import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  QrCode,
  Menu,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Clock,
  Copy,
  ExternalLink,
  CheckCircle,
  Share2,
  Download,
  Trash2,
  ArrowRightIcon,
  ArrowLeftIcon,
  ShoppingCart,
  Eye
} from 'lucide-react'
import QRCode from 'react-qr-code'
import {
  ChartBarIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useBusinessStore } from '../../core/stores/businessStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { Business, BUSINESS_TYPE_LABELS } from '../../core/models/business'
import Button from '../../components/ui/Button'
import EditButton from '../../components/buttons/EditButton'
import FilterButton from '../../components/buttons/FilterButton'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs'
import UploadLogoModal from '../../components/business/UploadLogoModal'
import Modal from '../../components/ui/Modal'
import Input from '../../components/ui/Input'
import DateInput from '../../components/ui/DateInput'
import { AppRoute } from '../../core/config/app-routes'
import toast from 'react-hot-toast'
import { i18n } from '../../core/i18n'

export default function BusinessDetailsPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const navigate = useNavigate()

  const {
    currentBusiness,
    businesses,
    isLoading,
    setCurrentBusiness,
    fetchBusinesses,
    updateBusiness,
    deleteBusiness
  } = useBusinessStore()
  const { t, isRTL } = useTranslation()

  const [uploadingLogo, setUploadingLogo] = useState<Business | null>(null)
  const [business, setBusiness] = useState<Business | null>(null)
  const [copiedSlug, setCopiedSlug] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteConfirmationSlug, setDeleteConfirmationSlug] = useState('')
  const [showFilterModal, setShowFilterModal] = useState(false)
  
  // Get first day of current month
  const getFirstDayOfMonth = () => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  }

  const [fromDate, setFromDate] = useState<Date | null>(getFirstDayOfMonth())
  const [toDate, setToDate] = useState<Date | null>(new Date())
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null)
  const [tempToDate, setTempToDate] = useState<Date | null>(null)

  // Copy current filters to temporary filters when modal opens
  useEffect(() => {
    if (showFilterModal) {
      // If no filters are set, use default (first day of month to today)
      if (!fromDate && !toDate) {
        setTempFromDate(getFirstDayOfMonth())
        setTempToDate(new Date())
      } else {
        setTempFromDate(fromDate)
        setTempToDate(toDate)
      }
    }
  }, [showFilterModal, fromDate, toDate])

  const handleApplyFilters = () => {
    if (tempFromDate && tempToDate && tempFromDate > tempToDate) {
      toast.error(t('invalidDateRange'))
      return
    }
    setFromDate(tempFromDate)
    setToDate(tempToDate)
    setShowFilterModal(false)
    toast.success(t('filterApplied'))
    // TODO: Fetch statistics with date range
  }

  const handleFilterModalClose = () => {
    // Reset temp filters to current filters when closing without applying
    setTempFromDate(fromDate)
    setTempToDate(toDate)
    setShowFilterModal(false)
  }

  // Get active filters as badges
  const getActiveFilters = () => {
    const filters: Array<{ id: string, label: string, type: string }> = []
    
    if (fromDate && toDate) {
      filters.push({
        id: 'date-range',
        label: `${new Date(fromDate).toLocaleDateString('en-US', { calendar: 'gregory' })} - ${new Date(toDate).toLocaleDateString('en-US', { calendar: 'gregory' })}`,
        type: 'date-range'
      })
    } else if (fromDate) {
      filters.push({
        id: 'from-date',
        label: `${t('fromDate')}: ${new Date(fromDate).toLocaleDateString('en-US', { calendar: 'gregory' })}`,
        type: 'from-date'
      })
    } else if (toDate) {
      filters.push({
        id: 'to-date',
        label: `${t('toDate')}: ${new Date(toDate).toLocaleDateString('en-US', { calendar: 'gregory' })}`,
        type: 'to-date'
      })
    }
    
    return filters
  }

  const removeFilter = (_filterId: string, filterType: string) => {
    if (filterType === 'date-range' || filterType === 'from-date') {
      setFromDate(null)
    }
    if (filterType === 'date-range' || filterType === 'to-date') {
      setToDate(null)
    }
    toast.success(t('filterCleared'))
    // TODO: Refetch statistics without filter
  }

  const activeFilters = getActiveFilters()

  useEffect(() => {
    const loadBusiness = async () => {
      if (!businessId) return

      const businessIdNum = parseInt(businessId)
      if (isNaN(businessIdNum)) return

      // Try to find business from businesses array first
      const foundBusiness = businesses.find(b => b.id === businessIdNum)
      if (foundBusiness) {
        setCurrentBusiness(foundBusiness)
        setBusiness(foundBusiness)
        return
      }

      // If not found and businesses array is empty or doesn't contain this business, fetch all businesses
      if (businesses.length === 0 || !businesses.some(b => b.id === businessIdNum)) {
        try {
          await fetchBusinesses()
          // After fetch, the second useEffect will handle updating the business
          // No need to manually check here as the effect will trigger
        } catch (error) {
          console.error('Failed to fetch businesses:', error)
        }
      }
    }

    loadBusiness()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId])

  // Update business when businesses array or currentBusiness changes
  useEffect(() => {
    if (businessId) {
      const businessIdNum = parseInt(businessId)
      if (isNaN(businessIdNum)) return

      const foundBusiness = businesses.find(b => b.id === businessIdNum)
      if (foundBusiness && (!business || business.id !== foundBusiness.id)) {
        setBusiness(foundBusiness)
        if (!currentBusiness || currentBusiness.id !== foundBusiness.id) {
          setCurrentBusiness(foundBusiness)
        }
      } else if (currentBusiness && currentBusiness.id === businessIdNum && !business) {
        setBusiness(currentBusiness)
      }
    }
  }, [businesses, currentBusiness, businessId, business])

  const handleUploadLogo = () => {
    if (business) {
      setUploadingLogo(business)
    }
  }

  const handleEditBusiness = () => {
    if (business) {
      navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE + '/' + business.id + '/edit')
    }
  }

  const handleToggleOrders = async () => {
    if (!business) return

    try {
      const updated = await updateBusiness(business.id, {
        isAcceptingOrders: !business.isAcceptingOrders
      })

      if (updated) {
        // Update local state
        setBusiness(prev => prev ? { ...prev, isAcceptingOrders: !prev.isAcceptingOrders } : null)
        if (currentBusiness && currentBusiness.id === business.id) {
          setCurrentBusiness({ ...currentBusiness, isAcceptingOrders: !currentBusiness.isAcceptingOrders })
        }

        toast.success(business.isAcceptingOrders ? t('notAcceptingOrders') : t('acceptingOrders'))
      }

    } catch (err: any) {
      console.error('Error updating business:', err)
      toast.error(err.response?.data?.error?.message || t('serverError'))
    }
  }

  const handleCopyLink = async () => {
    if (!business) return
    const menuUrl = business.slug
      ? `${window.location.origin}/${business.slug}`
      : `${window.location.origin}/${business.id}`
    try {
      await navigator.clipboard.writeText(menuUrl)
      toast.success(t('linkCopied'))
    } catch (err) {
      console.error('Failed to copy link:', err)
      toast.error(t('copyFailed'))
    }
  }

  const handleCopySlugLink = async () => {
    if (!business?.slug) return
    const menuUrl = `${window.location.origin}/${business.slug}`
    try {
      await navigator.clipboard.writeText(menuUrl)
      setCopiedSlug(true)
      toast.success(t('linkCopied'))
      setTimeout(() => setCopiedSlug(false), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
      toast.error(t('copyFailed'))
    }
  }

  const handleShare = async () => {
    if (!business) return
    const menuUrl = business.slug
      ? `${window.location.origin}/${business.slug}`
      : `${window.location.origin}/${business.id}`
    const shareData = {
      title: business.name,
      text: `${t('shareMenuWithCustomers')} - ${business.name}`,
      url: menuUrl
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        await handleCopyLink()
      }
    } catch (err) {
      console.error('Error sharing:', err)
    }
  }

  const handleDownloadQR = async () => {
    if (!business) return

    const menuUrl = business.slug
      ? `${window.location.origin}/${business.slug}`
      : `${window.location.origin}/${business.id}`

    // Create QR code URL using external service
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(menuUrl)}`

    try {
      // Fetch the QR code image
      const response = await fetch(qrCodeUrl)
      const blob = await response.blob()

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `qr-code-${business.name.replace(/\s+/g, '-')}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success(t('qrCodeDownloaded'))
    } catch (error) {
      console.error('Error downloading QR code:', error)
      toast.error(t('copyFailed'))
    }
  }

  const handleDeleteConfirm = async () => {
    if (!business) return

    // Validate slug confirmation
    const expectedSlug = business.slug || business.id.toString()
    if (deleteConfirmationSlug !== expectedSlug) {
      toast.error(t('slugMismatch'))
      return
    }

    setIsDeleting(true)
    try {
      const success = await deleteBusiness(business.id)
      if (success) {
        toast.success(i18n.translate('businessDeleted'))
        setShowDeleteModal(false)
        setDeleteConfirmationSlug('')
        navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)
      }
    } catch (error) {
      console.error('Error deleting business:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false)
    setDeleteConfirmationSlug('')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      calendar: 'gregory'
    })
  }

  if (!businessId) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('invalidBusinessId')}</p>
      </div>
    )
  }

  if (isLoading && !business) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!business) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('businessNotFound')}</p>
        <Button
          onClick={() => navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)}
          className="mt-4"
        >
          {t('back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={() => navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)}
          className="px-2 py-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {isRTL ? <ArrowRightIcon className="w-5 h-5" /> : <ArrowLeftIcon className="w-5 h-5" />}
        </button>
        <p className="text-gray-700 mt-1">
          {t('businessDetails')}
        </p>
      </div>

      {/* Tabs Container */}
      <Tabs defaultTab="info">
        <TabsList variant="horizontal">
          <TabsTrigger value="info" icon={<InformationCircleIcon className="w-4 h-4" />}>
            {t('basicInformation')}
          </TabsTrigger>
          <TabsTrigger value="qr" icon={<QrCode className="w-4 h-4" />}>
            {t('qrCode')}
          </TabsTrigger>
          <TabsTrigger value="delete" icon={<Trash2 className="w-4 h-4" />}>
            {t('deleteBusiness')}
          </TabsTrigger>
        </TabsList>

        {/* Information Tab */}
        <TabsContent value="info">
          <div className="space-y-4">
            {/* Business Status */}
            <Card>
              <CardContent>
                <div className="flex items-center justify-between pt-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${business.isAcceptingOrders ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {business.isAcceptingOrders ? t('acceptingOrders') : t('notAcceptingOrders')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {business.isAcceptingOrders ? t('acceptingOrders') : t('notAcceptingOrders')}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleToggleOrders}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${business.isAcceptingOrders
                      ? 'text-green-700 bg-green-100 hover:bg-green-200'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                      }`}
                  >
                    <span>{business.isAcceptingOrders ? t('stopAcceptingOrders') : t('startAcceptingOrders')}</span>
                    {business.isAcceptingOrders ? (
                      <ToggleRight className="w-4 h-4" />
                    ) : (
                      <ToggleLeft className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Business Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600" />
                    {t('basicInformation')}
                  </CardTitle>
                  {business && (
                    <EditButton
                      onClick={handleEditBusiness}
                      item={business}
                      className="rounded-md"
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">{t('businessName')}</p>
                    <p className="text-gray-600">{business.name}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">{t('businessType')}</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {BUSINESS_TYPE_LABELS[business.businessType as keyof typeof BUSINESS_TYPE_LABELS] || business.businessType}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">{t('address')}</p>
                    <p className="text-gray-600">{business.address}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">{t('phoneNumber')}</p>
                    <p className={`text-gray-600 font-mono ${isRTL ? 'text-right' : 'text-left'}`} dir="ltr">{business.contactNumber}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">{t('status')}</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${business.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                      }`}>
                      {business.isActive ? t('active') : t('inactive')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">{t('businessColor')}</p>
                    <div className="w-10 h-5 rounded-sm" style={{ backgroundColor: business.color }}></div>
                  </div>
                  {business.description && (
                    <div className="md:col-span-2">
                      <p className="font-medium text-gray-900 text-sm mb-1">{t('description')}</p>
                      <p className="text-gray-600">{business.description}</p>
                    </div>
                  )}
                  {business.slug && (
                    <div className="md:col-span-2">
                      <p className="font-medium text-gray-900 text-sm mb-2">{t('slug')}</p>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <a
                          href={`/${business.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                        >
                          {`${window.location.origin}/${business.slug}`}
                        </a>
                        <button
                          onClick={handleCopySlugLink}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                          title={copiedSlug ? t('copied') : t('copy')}
                        >
                          {copiedSlug ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <a
                          href={`/${business.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                          title={t('viewPublicMenu')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timestamps */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  {t('timestamps')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{t('createdAt')}</p>
                      <p className="text-gray-600 text-sm">{formatDate(business.createdAt)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{t('lastUpdated')}</p>
                      <p className="text-gray-600 text-sm">{formatDate(business.updatedAt)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* QR Code Tab */}
        <TabsContent value="qr">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className={`p-2 rounded-lg ${business.isAcceptingOrders ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <QrCode className={`w-5 h-5 ${business.isAcceptingOrders ? 'text-green-600' : 'text-gray-500'}`} />
                </div>
                {t('menuAccess')}
              </CardTitle>
              <CardDescription>
                {t('shareMenuWithCustomers')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - QR Code */}
                <div className="flex flex-col items-center justify-center">
                  <div className="relative mb-4">
                    <div className={`p-6 rounded-2xl shadow-lg border-2 ${business.isAcceptingOrders
                        ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                        : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-300'
                      }`}>
                      <div className="w-48 h-48 bg-white rounded-xl flex items-center justify-center shadow-inner p-4">
                        {business.slug ? (
                          <QRCode
                            value={`${window.location.origin}/${business.slug}`}
                            size={192}
                            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                            className={business.isAcceptingOrders ? 'opacity-100' : 'opacity-50'}
                          />
                        ) : (
                          <QRCode
                            value={`${window.location.origin}/${business.id}`}
                            size={192}
                            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                            className={business.isAcceptingOrders ? 'opacity-100' : 'opacity-50'}
                          />
                        )}
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full border-2 border-white ${business.isAcceptingOrders ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                  </div>
                  <p className="text-sm font-medium text-gray-700">{t('qrCodePreview')}</p>
                  <p className={`text-xs mt-1 ${business.isAcceptingOrders ? 'text-gray-500' : 'text-gray-400'}`}>
                    {business.isAcceptingOrders ? t('scanToViewMenu') : t('notAcceptingOrders')}
                  </p>
                </div>

                {/* Right Column - Link & Actions */}
                <div className="flex flex-col gap-6">
                  {/* Menu Link */}
                  {business.slug && (
                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Copy className="w-4 h-4 text-blue-600" />
                        {t('menuLink')}
                      </label>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <a
                          href={`/${business.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                        >
                          {`${window.location.origin}/${business.slug}`}
                        </a>
                        <button
                          onClick={handleCopySlugLink}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                          title={copiedSlug ? t('copied') : t('copy')}
                        >
                          {copiedSlug ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <a
                          href={`/${business.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                          title={t('viewPublicMenu')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      onClick={handleDownloadQR}
                      icon={<Download className="w-4 h-4" />}
                      iconPosition="left"
                      className="bg-[#1E293B] text-white hover:bg-[#0F172A] active:bg-[#020617] border-0"
                    >
                      {t('downloadQRCode')}
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShare}
                      icon={<Share2 className="w-4 h-4" />}
                      iconPosition="left"
                      className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    >
                      {t('shareMenu')}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delete Tab */}
        <TabsContent value="delete">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-red-100">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                {t('deleteBusiness')}
              </CardTitle>
              <CardDescription>
                {t('deleteBusinessMessage').replace('{name}', business.name)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Warning Section */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-2">
                      {t('deleteBusiness')}
                    </h3>
                    <p className="text-sm text-red-800 whitespace-pre-line">
                      {t('deleteBusinessDetails')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">{t('businessInformation')}</h4>
                <table className="w-full text-sm border-collapse">
                  <tbody>
                    <tr>
                      <td className="text-gray-600 py-2">{t('businessName')}:</td>
                      <td className="font-medium text-gray-900 py-2">{business.name}</td>
                    </tr>
                    {business.address && (
                      <tr>
                        <td className="text-gray-600 py-2">{t('address')}:</td>
                        <td className="font-medium text-gray-900 py-2">{business.address}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-gray-600 py-2">{t('status')}:</td>
                      <td className={`font-medium py-2 ${business.isActive ? 'text-green-600' : 'text-gray-600'}`}>
                        {business.isActive ? t('active') : t('inactive')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Delete Button */}
              <div className="pt-4 border-t border-gray-200 flex justify-end">
                <Button
                  onClick={() => setShowDeleteModal(true)}
                  variant="danger"
                  size="md"
                  icon={<Trash2 className="w-4 h-4" />}
                  iconPosition="left"
                  className="bg-red-600 text-white hover:bg-red-700 active:bg-red-800 border-0"
                >
                  {t('deleteBusiness')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showDeleteModal && business && (
        <Modal
          isOpen={showDeleteModal}
          onClose={handleDeleteModalClose}
          title={t('deleteBusiness')}
          description={t('deleteBusinessMessage').replace('{name}', business.name) + '\n\n' + t('deleteBusinessDetails')}
          actionButtonText={t('delete')}
          onAction={handleDeleteConfirm}
          actionButtonLoading={isDeleting}
          actionButtonDisabled={isDeleting || deleteConfirmationSlug !== (business.slug || business.id.toString())}
          actionButtonClassName="bg-red-600 text-white hover:bg-red-700 active:bg-red-800 text-sm h-9 py-2 px-4"
          showCancelButton={true}
        >
          <div className="space-y-4 pt-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                {t('confirmDeleteSlug')}
              </p>
              <p className="text-xs text-yellow-700 font-mono bg-yellow-100 px-2 py-1 rounded inline-block">
                {business.slug || business.id}
              </p>
            </div>
            <Input
              label={t('slug')}
              value={deleteConfirmationSlug}
              onChange={(e) => setDeleteConfirmationSlug(e.target.value)}
              placeholder={business.slug || business.id.toString()}
              disabled={isDeleting}
              className="font-mono"
            />
          </div>
        </Modal>
      )}

      {uploadingLogo && (
        <UploadLogoModal
          business={uploadingLogo}
          isOpen={!!uploadingLogo}
          onClose={async () => {
            setUploadingLogo(null)
            // Reload business data
            await fetchBusinesses()
            const updated = businesses.find(b => b.id === uploadingLogo.id)
            if (updated) {
              setCurrentBusiness(updated)
              setBusiness(updated)
            }
          }}
        />
      )}

      {/* Filter Modal */}
      <Modal
        isOpen={showFilterModal}
        onClose={handleFilterModalClose}
        title={t('filter')}
        showCancelButton={true}
        cancelButtonText={t('close')}
        actionButtonText={t('apply')}
        onAction={handleApplyFilters}
      >
        <div className="max-h-[70vh] overflow-y-auto">
          {/* Date Filters */}
          <div className="pb-4 border-b border-gray-200">
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DateInput
                  label={t('fromDate')}
                  selected={tempFromDate}
                  onChange={(date: Date | null) => setTempFromDate(date)}
                  selectsStart
                  startDate={tempFromDate}
                  endDate={tempToDate}
                  maxDate={tempToDate || new Date()}
                  placeholderText={t('fromDate')}
                  isClearable
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  className="text-sm"
                />
                <DateInput
                  label={t('toDate')}
                  selected={tempToDate}
                  onChange={(date: Date | null) => setTempToDate(date)}
                  selectsEnd
                  startDate={tempFromDate}
                  endDate={tempToDate}
                  minDate={tempFromDate}
                  maxDate={new Date()}
                  placeholderText={t('toDate')}
                  isClearable
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
